import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Info, Power, Tag, Copy } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { API, authFetch, formatDate } from './helpers';

const TicketSalesTab = () => {
  const [ticketTypes, setTicketTypes] = useState([]);
  const [ticketOrders, setTicketOrders] = useState([]);
  const [ticketSummary, setTicketSummary] = useState(null);
  const [salesSettings, setSalesSettings] = useState({ sales_enabled: true, closed_message: '', sales_start: '', sales_end: '', max_per_order: 10 });
  const [discountCodes, setDiscountCodes] = useState([]);

  const [showTicketForm, setShowTicketForm] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [ticketForm, setTicketForm] = useState({ name: '', description: '', price: 0, capacity: 0, is_active: true, sort_order: 0 });

  const [showOrderForm, setShowOrderForm] = useState(false);
  const [orderForm, setOrderForm] = useState({ ticket_type_id: '', buyer_name: '', buyer_email: '', buyer_phone: '', quantity: 1, notes: '' });

  const [showCodeForm, setShowCodeForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [codeForm, setCodeForm] = useState({ code: '', discount_type: 'percent', discount_value: 0, max_uses: 0, min_order: 0, is_active: true, expires_at: '' });

  const [settingsSaving, setSettingsSaving] = useState(false);

  const fetchData = async () => {
    const [typesRes, ordersRes, summaryRes, settingsRes, codesRes] = await Promise.all([
      authFetch(`${API}/ticket-types/all`),
      authFetch(`${API}/ticket-orders`),
      authFetch(`${API}/ticket-orders/summary`),
      authFetch(`${API}/ticket-settings`),
      authFetch(`${API}/discount-codes`)
    ]);
    if (typesRes.ok) setTicketTypes(await typesRes.json());
    if (ordersRes.ok) setTicketOrders(await ordersRes.json());
    if (summaryRes.ok) setTicketSummary(await summaryRes.json());
    if (settingsRes.ok) {
      const s = await settingsRes.json();
      setSalesSettings({ sales_enabled: s.sales_enabled ?? true, closed_message: s.closed_message || '', sales_start: s.sales_start || '', sales_end: s.sales_end || '', max_per_order: s.max_per_order ?? 10 });
    }
    if (codesRes.ok) setDiscountCodes(await codesRes.json());
  };

  useEffect(() => { fetchData(); }, []);

  // Sales settings
  const toggleSales = async () => {
    const newVal = !salesSettings.sales_enabled;
    setSalesSettings(prev => ({ ...prev, sales_enabled: newVal }));
    await authFetch(`${API}/ticket-settings`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sales_enabled: newVal }) });
  };

  const saveSalesSettings = async () => {
    setSettingsSaving(true);
    await authFetch(`${API}/ticket-settings`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(salesSettings) });
    setSettingsSaving(false);
  };

  // Ticket types
  const openNewTicketForm = () => { setEditingTicket(null); setTicketForm({ name: '', description: '', price: 0, capacity: 0, is_active: true, sort_order: 0 }); setShowTicketForm(true); };
  const openEditTicketForm = (t) => { setEditingTicket(t); setTicketForm({ name: t.name, description: t.description || '', price: t.price, capacity: t.capacity, is_active: t.is_active, sort_order: t.sort_order || 0 }); setShowTicketForm(true); };

  const saveTicketType = async (e) => {
    e.preventDefault();
    if (editingTicket) {
      await authFetch(`${API}/ticket-types/${editingTicket.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(ticketForm) });
    } else {
      await authFetch(`${API}/ticket-types`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(ticketForm) });
    }
    setShowTicketForm(false); setEditingTicket(null); fetchData();
  };

  const deleteTicketType = async (id) => { if (!window.confirm('Delete this ticket type?')) return; await authFetch(`${API}/ticket-types/${id}`, { method: 'DELETE' }); fetchData(); };

  // Orders
  const saveOrder = async (e) => {
    e.preventDefault();
    await authFetch(`${API}/ticket-orders`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(orderForm) });
    setShowOrderForm(false); setOrderForm({ ticket_type_id: '', buyer_name: '', buyer_email: '', buyer_phone: '', quantity: 1, notes: '' }); fetchData();
  };

  const deleteOrder = async (id) => { if (!window.confirm('Delete this order?')) return; await authFetch(`${API}/ticket-orders/${id}`, { method: 'DELETE' }); fetchData(); };

  // Discount codes
  const openNewCodeForm = () => { setEditingCode(null); setCodeForm({ code: '', discount_type: 'percent', discount_value: 0, max_uses: 0, min_order: 0, is_active: true, expires_at: '' }); setShowCodeForm(true); };
  const openEditCodeForm = (c) => { setEditingCode(c); setCodeForm({ code: c.code, discount_type: c.discount_type, discount_value: c.discount_value, max_uses: c.max_uses || 0, min_order: c.min_order || 0, is_active: c.is_active, expires_at: c.expires_at || '' }); setShowCodeForm(true); };

  const saveCode = async (e) => {
    e.preventDefault();
    if (editingCode) {
      await authFetch(`${API}/discount-codes/${editingCode.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(codeForm) });
    } else {
      await authFetch(`${API}/discount-codes`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(codeForm) });
    }
    setShowCodeForm(false); setEditingCode(null); fetchData();
  };

  const deleteCode = async (id) => { if (!window.confirm('Delete this discount code?')) return; await authFetch(`${API}/discount-codes/${id}`, { method: 'DELETE' }); fetchData(); };

  const copyCode = (code) => { navigator.clipboard.writeText(code); };

  return (
    <div data-testid="tickets-tab">
      {/* Sales Master Controls */}
      <div className={`p-5 mb-6 border-2 ${salesSettings.sales_enabled ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="sales-controls">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Power className={`w-5 h-5 ${salesSettings.sales_enabled ? 'text-green-600' : 'text-red-600'}`} />
            <h2 className="text-lg font-semibold text-brand-dark">Ticket Sales</h2>
            <span className={`px-3 py-1 text-xs font-bold uppercase tracking-wider ${salesSettings.sales_enabled ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
              {salesSettings.sales_enabled ? 'LIVE' : 'OFF'}
            </span>
          </div>
          <button onClick={toggleSales} className={`px-5 py-2 text-sm font-semibold ${salesSettings.sales_enabled ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-green-600 text-white hover:bg-green-700'}`} data-testid="toggle-sales-btn">
            {salesSettings.sales_enabled ? 'Turn Sales OFF' : 'Turn Sales ON'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label className="text-brand-dark font-medium text-xs">Closed Message</Label>
            <Input value={salesSettings.closed_message} onChange={(e) => setSalesSettings({...salesSettings, closed_message: e.target.value})} className="mt-1 text-sm" placeholder="Message when sales are off" data-testid="closed-message-input" />
          </div>
          <div>
            <Label className="text-brand-dark font-medium text-xs">Sales Start Date</Label>
            <Input type="date" value={salesSettings.sales_start} onChange={(e) => setSalesSettings({...salesSettings, sales_start: e.target.value})} className="mt-1 text-sm" data-testid="sales-start-input" />
          </div>
          <div>
            <Label className="text-brand-dark font-medium text-xs">Sales End Date</Label>
            <Input type="date" value={salesSettings.sales_end} onChange={(e) => setSalesSettings({...salesSettings, sales_end: e.target.value})} className="mt-1 text-sm" data-testid="sales-end-input" />
          </div>
          <div>
            <Label className="text-brand-dark font-medium text-xs">Max Tickets Per Order</Label>
            <Input type="number" min="1" value={salesSettings.max_per_order} onChange={(e) => setSalesSettings({...salesSettings, max_per_order: parseInt(e.target.value) || 1})} className="mt-1 text-sm" data-testid="max-per-order-input" />
          </div>
        </div>
        <button onClick={saveSalesSettings} className="mt-3 bg-brand-dark text-white px-4 py-1.5 text-xs font-medium hover:opacity-90" data-testid="save-sales-settings-btn">{settingsSaving ? 'Saving...' : 'Save Settings'}</button>
      </div>

      {/* Summary */}
      {ticketSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="ticket-summary">
          <div className="bg-white p-5"><p className="text-2xl font-semibold text-brand-gold">${(ticketSummary.total_revenue || 0).toFixed(2)}</p><p className="text-sm text-gray-600">Total Revenue</p></div>
          <div className="bg-white p-5"><p className="text-2xl font-semibold text-brand-gold">{ticketSummary.total_tickets_sold || 0}</p><p className="text-sm text-gray-600">Tickets Sold</p></div>
          <div className="bg-white p-5"><p className="text-2xl font-semibold text-brand-gold">{ticketSummary.total_capacity || 0}</p><p className="text-sm text-gray-600">Total Capacity</p></div>
          <div className="bg-white p-5"><p className="text-2xl font-semibold text-brand-gold">{ticketSummary.total_orders || 0}</p><p className="text-sm text-gray-600">Orders</p></div>
        </div>
      )}

      {/* Discount Codes */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><Tag className="w-5 h-5 text-brand-gold" /><h2 className="text-xl font-semibold text-brand-dark">Discount Codes</h2></div>
          <button onClick={openNewCodeForm} className="flex items-center gap-2 bg-brand-gold px-4 py-2 text-sm font-medium text-brand-dark hover:opacity-90" data-testid="add-discount-btn"><Plus className="w-4 h-4" /> New Code</button>
        </div>

        {showCodeForm && (
          <div className="bg-white p-6 mb-4" data-testid="discount-form">
            <h3 className="text-lg font-medium text-brand-dark mb-4">{editingCode ? 'Edit Discount Code' : 'Create Discount Code'}</h3>
            <form onSubmit={saveCode} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><Label className="text-brand-dark font-medium">Code</Label><Input required value={codeForm.code} onChange={(e) => setCodeForm({...codeForm, code: e.target.value.toUpperCase()})} className="mt-1 font-mono" placeholder="EARLYBIRD" data-testid="discount-code-input" /></div>
                <div>
                  <Label className="text-brand-dark font-medium">Discount Type</Label>
                  <select value={codeForm.discount_type} onChange={(e) => setCodeForm({...codeForm, discount_type: e.target.value})} className="mt-1 w-full border border-gray-300 p-2 rounded" data-testid="discount-type-select">
                    <option value="percent">Percentage (%)</option><option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>
                <div><Label className="text-brand-dark font-medium">{codeForm.discount_type === 'percent' ? 'Discount (%)' : 'Discount ($)'}</Label><Input type="number" step="0.01" min="0" required value={codeForm.discount_value} onChange={(e) => setCodeForm({...codeForm, discount_value: parseFloat(e.target.value) || 0})} className="mt-1" data-testid="discount-value-input" /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><Label className="text-brand-dark font-medium">Max Uses (0 = unlimited)</Label><Input type="number" min="0" value={codeForm.max_uses} onChange={(e) => setCodeForm({...codeForm, max_uses: parseInt(e.target.value) || 0})} className="mt-1" data-testid="discount-max-uses" /></div>
                <div><Label className="text-brand-dark font-medium">Min Order Amount ($)</Label><Input type="number" step="0.01" min="0" value={codeForm.min_order} onChange={(e) => setCodeForm({...codeForm, min_order: parseFloat(e.target.value) || 0})} className="mt-1" /></div>
                <div><Label className="text-brand-dark font-medium">Expires</Label><Input type="date" value={codeForm.expires_at} onChange={(e) => setCodeForm({...codeForm, expires_at: e.target.value})} className="mt-1" data-testid="discount-expires" /></div>
              </div>
              <div className="flex items-center gap-3">
                <input type="checkbox" id="codeActive" checked={codeForm.is_active} onChange={(e) => setCodeForm({...codeForm, is_active: e.target.checked})} className="w-4 h-4" />
                <Label htmlFor="codeActive">Active</Label>
              </div>
              <div className="flex gap-4">
                <button type="submit" className="bg-brand-gold px-6 py-2 text-sm font-medium text-brand-dark hover:opacity-90" data-testid="save-discount-btn">{editingCode ? 'Update' : 'Create'}</button>
                <button type="button" onClick={() => { setShowCodeForm(false); setEditingCode(null); }} className="bg-gray-200 px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {discountCodes.length === 0 ? (
          <div className="text-center py-6 bg-white"><p className="text-gray-500 text-sm">No discount codes yet.</p></div>
        ) : (
          <div className="space-y-2" data-testid="discount-codes-list">
            {discountCodes.map(c => (
              <div key={c.id} className={`bg-white p-4 flex items-center justify-between ${!c.is_active ? 'opacity-50' : ''}`} data-testid={`discount-${c.id}`}>
                <div className="flex items-center gap-4">
                  <button onClick={() => copyCode(c.code)} className="font-mono text-sm bg-gray-100 px-3 py-1 hover:bg-gray-200 flex items-center gap-1" title="Copy code"><Copy className="w-3 h-3" /> {c.code}</button>
                  <span className="text-brand-gold font-semibold">{c.discount_type === 'percent' ? `${c.discount_value}%` : `$${c.discount_value.toFixed(2)}`} off</span>
                  <span className="text-xs text-gray-500">{c.uses}{c.max_uses > 0 ? `/${c.max_uses}` : ''} used</span>
                  {c.expires_at && <span className="text-xs text-gray-400">Expires {c.expires_at}</span>}
                  {!c.is_active && <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500">Inactive</span>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEditCodeForm(c)} className="p-2 text-gray-400 hover:text-brand-dark"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => deleteCode(c.id)} className="p-2 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ticket Types */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-brand-dark">Ticket Types</h2>
          <button onClick={openNewTicketForm} className="flex items-center gap-2 bg-brand-gold px-4 py-2 text-sm font-medium text-brand-dark hover:opacity-90" data-testid="add-ticket-type-btn"><Plus className="w-4 h-4" /> Add Ticket Type</button>
        </div>

        {showTicketForm && (
          <div className="bg-white p-6 mb-4" data-testid="ticket-type-form">
            <h3 className="text-lg font-medium text-brand-dark mb-4">{editingTicket ? 'Edit Ticket Type' : 'New Ticket Type'}</h3>
            <form onSubmit={saveTicketType} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label className="text-brand-dark font-medium">Name</Label><Input required value={ticketForm.name} onChange={(e) => setTicketForm({...ticketForm, name: e.target.value})} className="mt-1" placeholder="e.g., Weekend Pass" data-testid="ticket-name-input" /></div>
                <div><Label className="text-brand-dark font-medium">Price ($)</Label><Input type="number" step="0.01" min="0" value={ticketForm.price} onChange={(e) => setTicketForm({...ticketForm, price: parseFloat(e.target.value) || 0})} className="mt-1" data-testid="ticket-price-input" /></div>
                <div><Label className="text-brand-dark font-medium">Capacity (0 = unlimited)</Label><Input type="number" min="0" value={ticketForm.capacity} onChange={(e) => setTicketForm({...ticketForm, capacity: parseInt(e.target.value) || 0})} className="mt-1" data-testid="ticket-capacity-input" /></div>
                <div><Label className="text-brand-dark font-medium">Sort Order</Label><Input type="number" value={ticketForm.sort_order} onChange={(e) => setTicketForm({...ticketForm, sort_order: parseInt(e.target.value) || 0})} className="mt-1" /></div>
              </div>
              <div><Label className="text-brand-dark font-medium">Description</Label><Textarea value={ticketForm.description} onChange={(e) => setTicketForm({...ticketForm, description: e.target.value})} className="mt-1" rows={2} data-testid="ticket-desc-input" /></div>
              <div className="flex items-center gap-3">
                <input type="checkbox" id="ticketActive" checked={ticketForm.is_active} onChange={(e) => setTicketForm({...ticketForm, is_active: e.target.checked})} className="w-4 h-4" />
                <Label htmlFor="ticketActive">Active (visible on public page)</Label>
              </div>
              <div className="flex gap-4">
                <button type="submit" className="bg-brand-gold px-6 py-2 text-sm font-medium text-brand-dark hover:opacity-90" data-testid="save-ticket-type-btn">{editingTicket ? 'Update' : 'Create'}</button>
                <button type="button" onClick={() => { setShowTicketForm(false); setEditingTicket(null); }} className="bg-gray-200 px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {ticketTypes.length === 0 ? (
          <div className="text-center py-8 bg-white"><p className="text-gray-500">No ticket types yet.</p></div>
        ) : (
          <div className="space-y-3" data-testid="ticket-types-list">
            {ticketTypes.map(t => (
              <div key={t.id} className={`bg-white p-4 flex items-center justify-between ${!t.is_active ? 'opacity-50' : ''}`} data-testid={`ticket-type-${t.id}`}>
                <div>
                  <div className="flex items-center gap-3"><h3 className="font-medium text-brand-dark">{t.name}</h3><span className="text-brand-gold font-semibold">${(t.price || 0).toFixed(2)}</span>{!t.is_active && <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500">Inactive</span>}</div>
                  <p className="text-sm text-gray-500">{t.sold || 0} sold{t.capacity > 0 ? ` / ${t.capacity} capacity` : ''}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEditTicketForm(t)} className="p-2 text-gray-400 hover:text-brand-dark" data-testid={`edit-ticket-${t.id}`}><Edit className="w-4 h-4" /></button>
                  <button onClick={() => deleteTicketType(t.id)} className="p-2 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Orders */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-brand-dark">Orders</h2>
          <button onClick={() => setShowOrderForm(!showOrderForm)} className="flex items-center gap-2 bg-brand-dark px-4 py-2 text-sm font-medium text-white hover:opacity-90" data-testid="add-order-btn"><Plus className="w-4 h-4" /> Log Order</button>
        </div>

        {showOrderForm && (
          <div className="bg-white p-6 mb-4" data-testid="order-form">
            <h3 className="text-lg font-medium text-brand-dark mb-4">Log New Order</h3>
            <form onSubmit={saveOrder} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-brand-dark font-medium">Ticket Type</Label>
                  <select required value={orderForm.ticket_type_id} onChange={(e) => setOrderForm({...orderForm, ticket_type_id: e.target.value})} className="mt-1 w-full border border-gray-300 p-2 rounded" data-testid="order-ticket-select">
                    <option value="">Select ticket type</option>
                    {ticketTypes.filter(t => t.is_active).map(t => (<option key={t.id} value={t.id}>{t.name} - ${t.price}</option>))}
                  </select>
                </div>
                <div><Label className="text-brand-dark font-medium">Quantity</Label><Input type="number" min="1" value={orderForm.quantity} onChange={(e) => setOrderForm({...orderForm, quantity: parseInt(e.target.value) || 1})} className="mt-1" data-testid="order-qty-input" /></div>
                <div><Label className="text-brand-dark font-medium">Buyer Name</Label><Input required value={orderForm.buyer_name} onChange={(e) => setOrderForm({...orderForm, buyer_name: e.target.value})} className="mt-1" data-testid="order-name-input" /></div>
                <div><Label className="text-brand-dark font-medium">Buyer Email</Label><Input type="email" required value={orderForm.buyer_email} onChange={(e) => setOrderForm({...orderForm, buyer_email: e.target.value})} className="mt-1" data-testid="order-email-input" /></div>
              </div>
              <div><Label className="text-brand-dark font-medium">Notes</Label><Input value={orderForm.notes} onChange={(e) => setOrderForm({...orderForm, notes: e.target.value})} className="mt-1" placeholder="Payment reference, special requests, etc." /></div>
              <div className="flex gap-4">
                <button type="submit" className="bg-brand-gold px-6 py-2 text-sm font-medium text-brand-dark hover:opacity-90" data-testid="save-order-btn">Log Order</button>
                <button type="button" onClick={() => setShowOrderForm(false)} className="bg-gray-200 px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {ticketOrders.length === 0 ? (
          <div className="text-center py-8 bg-white"><p className="text-gray-500">No orders yet.</p></div>
        ) : (
          <div className="bg-white overflow-x-auto" data-testid="orders-list">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left">
                  <th className="px-4 py-3 font-medium text-gray-600">Buyer</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Ticket</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Qty</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Total</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Method</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Date</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {ticketOrders.map(order => (
                  <tr key={order.id} className="border-b border-gray-100" data-testid={`order-${order.id}`}>
                    <td className="px-4 py-3"><p className="font-medium text-brand-dark">{order.buyer_name}</p><p className="text-xs text-gray-500">{order.buyer_email}</p></td>
                    <td className="px-4 py-3 text-gray-700">{order.ticket_type_name}</td>
                    <td className="px-4 py-3 text-gray-700">{order.quantity}</td>
                    <td className="px-4 py-3 font-medium text-brand-gold">${(order.total_price || 0).toFixed(2)}</td>
                    <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600">{order.payment_method || 'stripe'}</span></td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 ${order.payment_status === 'completed' ? 'bg-green-100 text-green-800' : order.payment_status === 'refunded' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>{order.payment_status}</span></td>
                    <td className="px-4 py-3 text-xs text-gray-500">{formatDate(order.created_at)}</td>
                    <td className="px-4 py-3"><button onClick={() => deleteOrder(order.id)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketSalesTab;
